import { useRef } from 'react';
import { cn } from '@reflex/ui/lib/utils';
import { useLenis } from '@/hooks/ui/useLenis';
import {
  PageHeader as SharedPageHeader,
  pageBodyUnderMobileChrome,
  type PageHeaderProps as SharedPageHeaderProps,
} from '@reflex/ui/components/layout/PageLayout';
import { useRegisterPageChrome } from '@/components/layout/TakeoverShell/page-chrome';

const maxWidthClasses = {
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: '',
} as const;

interface PageLayoutProps {
  children: React.ReactNode;
  maxWidth?: keyof typeof maxWidthClasses;
  className?: string;
}

export function PageLayout({ children, maxWidth = 'full', className }: PageLayoutProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useLenis(scrollRef, { allowNestedScroll: true });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div
        ref={scrollRef}
        className="flex w-full flex-1 flex-col overflow-y-auto"
        tabIndex={0}
        role="region"
        aria-label="Page content"
      >
        <div
          className={cn(
            'flex w-full flex-1 flex-col gap-6 px-4 pb-4 pt-3 md:p-6',
            pageBodyUnderMobileChrome,
            maxWidth !== 'full' && `mx-auto ${maxWidthClasses[maxWidth]}`,
            className,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

type PageHeaderProps = Pick<
  SharedPageHeaderProps,
  | 'icon'
  | 'iconVariant'
  | 'title'
  | 'description'
  | 'actions'
  | 'docs'
  | 'howToUse'
  | 'titleInMobileChrome'
>;

/**
 * Host-app page header. Delegates to the shared `@reflex/ui` `PageHeader`,
 * which hoists docs / description into takeover chrome. This wrapper also
 * publishes `title` so nested host pages replace the nav label (plugin
 * pages leave the shell label, e.g. "Model Costs").
 */
export function PageHeader(props: PageHeaderProps) {
  useRegisterPageChrome({ title: props.title });
  return <SharedPageHeader {...props} />;
}
